import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Loader2, AlertCircle, X, Link2 } from 'lucide-react';
import { toast } from 'sonner';

// Editor for the invoice(s) a payment is marked against.
//
// Before this, an invoice picked when the payment was recorded was permanent —
// a wrong pick could only be fixed by deleting and re-keying the payment. Here the
// user unticks (unmarks) the current invoice and ticks another; on save the backend
// releases the old invoice (its paid_amount / outstanding / status are reversed, so
// it is payable again) and re-applies the money to the new selection in one step,
// so nothing is ever credited twice.
//
// Shared by Payments -> Edit and Bank Reconciliation -> Edit so both behave the same.

const money = (amount, currency = 'USD') => {
  if (amount === null || amount === undefined || amount === '') return '-';
  return `${currency} ${Number(amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const sameSet = (a, b) => a.length === b.length && a.every((x) => b.includes(x));

const InvoiceMappingEditor = ({
  paymentId,
  canEdit = false,
  // Amount/currency currently typed in the parent's edit form. When they differ
  // from what is stored, the money on the invoices is stale and must be re-spread.
  expectedAmount = null,
  expectedCurrency = null,
  onBeforeSave = null,   // e.g. persist the parent's field edits first; may be async
  onSaved = null,        // notify the parent so it can refresh its list
  title = 'Invoice Mapping',
}) => {
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!paymentId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/payments/${paymentId}/invoice-mapping`);
      setView(res.data);
      setSelected(res.data.mapped_invoice_ids || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load invoice mapping');
      setView(null);
    } finally {
      setLoading(false);
    }
  }, [paymentId]);

  useEffect(() => { load(); }, [load]);

  if (!paymentId) return null;

  const payment = view?.payment;
  const available = view?.available || [];
  const mapped = view?.mapped || [];
  const currency = payment?.currency || 'USD';

  const originalIds = view?.mapped_invoice_ids || [];
  const selectionChanged = !sameSet(selected, originalIds);
  // A changed amount/currency leaves the invoices holding the old figure until the
  // mapping is saved again — flag it rather than silently re-writing invoices.
  // A half-typed / cleared amount box is ignored — only a real number counts as a change.
  const amountChanged = !!payment && (
    (Number.isFinite(Number(expectedAmount)) && expectedAmount !== null && expectedAmount !== ''
      && Number(expectedAmount) !== Number(payment.amount)) ||
    (!!expectedCurrency && expectedCurrency !== payment.currency)
  );
  const dirty = selectionChanged || amountChanged;

  const released = originalIds.filter((id) => !selected.includes(id));
  const addedCount = selected.filter((id) => !originalIds.includes(id)).length;

  const labelFor = (id) => {
    const inv = available.find((i) => i.invoice_id === id) || mapped.find((i) => i.invoice_id === id);
    return inv?.invoice_number || id;
  };

  const toggle = (invoiceId) => {
    setSelected((prev) => (prev.includes(invoiceId)
      ? prev.filter((x) => x !== invoiceId)
      : [...prev, invoiceId]));
  };

  const handleSave = async () => {
    if (released.length) {
      const names = released.map(labelFor).join(', ');
      const msg = selected.length
        ? `Unmark ${names} and mark this payment against ${selected.length} invoice(s) instead?\n\nThe unmarked invoice(s) will become outstanding again and available for other payments.`
        : `Unmark ${names}?\n\nThe invoice(s) will become outstanding again and this payment will be left as unallocated credit.`;
      if (!window.confirm(msg)) return;
    }
    setSaving(true);
    try {
      // Persist the parent's amount/currency edit first, so the money re-spread
      // below uses the figure the user just typed.
      if (amountChanged && onBeforeSave) await onBeforeSave();
      const res = await api.put(`/payments/${paymentId}/invoice-mapping`, { invoice_ids: selected });
      setView(res.data);
      setSelected(res.data.mapped_invoice_ids || []);
      toast.success(res.data.message || 'Invoice mapping updated');
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update invoice mapping');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 rounded-lg border border-[#D8D9E8] p-3" data-testid="invoice-mapping-editor">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link2 className="w-4 h-4 text-[#3E3F96]" />
          <span className="text-sm font-semibold text-[#1C1F1D]">{title}</span>
        </div>
        {payment && (
          <span className="text-xs text-[#5C635F]">
            {payment.receipt_number} · {money(payment.amount, currency)}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#5C635F] py-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading invoice mapping...
        </div>
      ) : error ? (
        <p className="text-sm text-[#C85A40]">{error}</p>
      ) : (
        <>
          {/* Currently marked against */}
          <div className="space-y-1">
            <p className="text-xs uppercase font-semibold text-[#5C635F]">Marked against</p>
            {mapped.length === 0 ? (
              <p className="text-sm text-[#5C635F]">
                Not marked against any invoice — the full amount sits as unallocated credit.
              </p>
            ) : (
              mapped.map((inv) => {
                const stillSelected = selected.includes(inv.invoice_id);
                return (
                  <div
                    key={inv.invoice_id}
                    className={`rounded px-2 py-1 text-sm ${
                      stillSelected ? 'bg-[#F5F5FA]' : 'bg-[#C85A40]/5 line-through text-[#5C635F]'
                    }`}
                    data-testid={`mapped-invoice-${inv.invoice_id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                    <span className="truncate">
                      <span className="font-medium">{inv.invoice_number}</span>
                      <span className="text-[#5C635F]"> · {inv.term || ''} {inv.academic_year || ''}</span>
                    </span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="tabular-nums">{money(inv.allocated_amount, inv.currency)}</span>
                      {canEdit && stillSelected && (
                        <Button
                          type="button" variant="ghost" size="sm" className="h-6 px-2 text-[#C85A40]"
                          onClick={() => toggle(inv.invoice_id)}
                          data-testid={`unmark-invoice-${inv.invoice_id}`}
                          title="Unmark this invoice"
                        >
                          <X className="w-3.5 h-3.5 mr-1" /> Unmark
                        </Button>
                      )}
                      {canEdit && !stillSelected && (
                        <Button
                          type="button" variant="ghost" size="sm" className="h-6 px-2 text-[#3E3F96]"
                          onClick={() => toggle(inv.invoice_id)}
                          data-testid={`restore-invoice-${inv.invoice_id}`}
                        >
                          Undo
                        </Button>
                      )}
                    </span>
                    </div>
                    {/* Read-only: the invoice's OWN figures. The amount above is only
                        this receipt's share, which on a part-settled invoice is far
                        smaller than the invoice and used to read as the invoice value. */}
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-[#5C635F] pl-0.5"
                      data-testid={`mapped-invoice-figures-${inv.invoice_id}`}>
                      <span>Invoice value: <span className="tabular-nums font-medium text-[#1C1F1D]">
                        {money(inv.amount, inv.currency)}</span></span>
                      <span>Paid in total: <span className="tabular-nums">
                        {money(inv.paid_amount, inv.currency)}</span></span>
                      <span>This receipt: <span className="tabular-nums">
                        {money(inv.allocated_amount, inv.currency)}</span></span>
                      <span>Balance due: <span className={`tabular-nums font-medium ${
                        Number(inv.outstanding) > 0 ? 'text-[#C85A40]' : 'text-[#2E7D6B]'}`}>
                        {money(inv.outstanding, inv.currency)}</span></span>
                    </div>
                  </div>
                );
              })
            )}
            {payment?.unallocated_amount > 0 && (
              <p className="text-xs text-[#D99C3B]">
                Unallocated credit on this payment: {money(payment.unallocated_amount, currency)}
              </p>
            )}
          </div>

          {/* Pick the invoice(s) to mark it against */}
          {canEdit && (
            <div className="space-y-1">
              <p className="text-xs uppercase font-semibold text-[#5C635F]">
                Mark against{' '}
                <span className="normal-case font-normal">
                  — {available.length} selectable, oldest term first
                </span>
              </p>
              {available.length === 0 ? (
                <p className="text-sm text-[#5C635F]">
                  No open invoices for this student to map this payment to.
                </p>
              ) : (
                <div className="max-h-56 overflow-y-auto rounded border border-[#D8D9E8] divide-y divide-[#EEF]">
                  {available.map((inv) => (
                    <label
                      key={inv.invoice_id}
                      className="flex items-center gap-2 px-2 py-1.5 hover:bg-[#F5F5FA] cursor-pointer text-sm"
                      data-testid={`map-invoice-${inv.invoice_id}`}
                    >
                      <Checkbox
                        checked={selected.includes(inv.invoice_id)}
                        onCheckedChange={() => toggle(inv.invoice_id)}
                      />
                      <span className="flex-1 truncate">
                        <span className="font-medium">{inv.invoice_number}</span>
                        <span className="text-[#5C635F]"> · {inv.term || ''} {inv.academic_year || ''}</span>
                        {inv.allocated_amount > 0 && (
                          <Badge variant="outline" className="ml-2 text-[10px] py-0">current</Badge>
                        )}
                      </span>
                      <span className="shrink-0 text-right">
                        {/* Invoice value first, then what it would owe — so a small
                            due figure is never mistaken for the invoice itself. */}
                        <span className="block text-[11px] text-[#5C635F] tabular-nums">
                          of {money(inv.amount, inv.currency)}
                        </span>
                        <span className="block tabular-nums text-[#C85A40] font-medium">
                          {money(inv.outstanding_if_released, inv.currency)} due
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
              <p className="text-xs text-[#5C635F]">
                The red figure is what each invoice owes once this payment releases it;
                "of" is the invoice's full value. Unticked invoices are released and become
                available for other payments.
              </p>
            </div>
          )}

          {canEdit && amountChanged && (
            <p className="text-xs text-[#D99C3B] flex items-start gap-1" data-testid="mapping-amount-changed">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                The amount/currency was changed in this form. Save the mapping to re-spread
                {' '}{money(expectedAmount, expectedCurrency || currency)} across the ticked invoice(s).
              </span>
            </p>
          )}

          {canEdit && (
            <div className="flex items-center justify-between gap-2 pt-1">
              <span className="text-xs text-[#5C635F]">
                {selectionChanged
                  ? `${released.length} to unmark, ${addedCount} to mark`
                  : 'No mapping changes'}
              </span>
              <div className="flex gap-2">
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={() => setSelected(originalIds)}
                  disabled={saving || !selectionChanged}
                >
                  Reset
                </Button>
                <Button
                  type="button" size="sm" className="bg-[#3E3F96] hover:bg-[#2D2E7A]"
                  onClick={handleSave} disabled={saving || !dirty}
                  data-testid="save-invoice-mapping"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save Mapping
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InvoiceMappingEditor;
